/** Canonical skill contract. It is intentionally data-only; execution remains a later boundary. */
import type { EffectNode, LeafEffect, PierceFalloff, Selector, Skill } from '../types.js';

export const CANONICAL_SKILL_V2_SCHEMA_VERSION = 'canonical-skill-v2' as const;
declare const validatedCanonicalSkillV2: unique symbol;

export type CanonicalPlacementV2 =
  | { readonly kind: 'CASTER' }
  | { readonly kind: 'RESOLVED_ACTOR' }
  | { readonly kind: 'RESOLVED_POINT' }
  | { readonly kind: 'CONTACT_POINT' }
  | { readonly kind: 'MOVING_ATTACK' };

export type CanonicalGeometryV2 =
  | { readonly owner: 'SKILL_OWNED'; readonly shape:
      | { readonly kind: 'POINT' }
      | { readonly kind: 'CIRCLE'; readonly radiusM: number }
      | { readonly kind: 'SECTOR'; readonly reachM: number; readonly angleDeg: number }
      | { readonly kind: 'CAPSULE'; readonly lengthM: number; readonly widthM: number } }
  | {
      readonly owner: 'WEAPON_CONTACT';
      readonly profile: 'EQUIPPED_WEAPON';
      readonly profileId?: string;
      readonly regionId?: string;
      readonly scales: { readonly reach: number; readonly width: number; readonly angle: number };
      readonly contactBindings?: readonly ContactEffectBindingV2[];
    };

export type ContactRegionRoleV2 = 'PRIMARY' | 'SECONDARY';
export type ContactRegionSelectorV2 =
  | { readonly kind: 'ROLE'; readonly role: ContactRegionRoleV2 }
  | { readonly kind: 'PROFILE_REGION'; readonly profileId: string; readonly regionId: string };
export interface ContactEffectBindingV2 {
  readonly selector: ContactRegionSelectorV2;
  readonly mode: 'APPEND' | 'REPLACE_DEFAULT';
  readonly effects: readonly CanonicalEffectNodeV2[];
}

export type CanonicalMotionV2 =
  | { readonly kind: 'WEAPON_SWEEP'; readonly durationS: number }
  | { readonly kind: 'INSTANT' }
  /** A weapon-contact thrust: the equipped weapon's straight corridor, resolved at once. */
  | { readonly kind: 'THRUST' }
  | {
      readonly kind: 'PROJECTILE';
      readonly maxTravelM: number;
      readonly speedMps: number;
      readonly collisionRadiusM: number;
      readonly replicationMode?: import('../types.js').AttackReplicationMode;
      readonly sourceLifetimePolicy?: 'EXPIRE' | 'CONTINUE_LAST_DIRECTION';
    };

/**
 * `targetCap` is deliberately absent: an area application's reach is its area
 * against the encounter density (E[N]), never an integer ceiling. See the
 * `Delivery` doc in `../types.ts`.
 */
export interface CanonicalContactPolicyV2 {
  readonly hitPolicy: 'ONCE_PER_TARGET' | 'COOLDOWN_PER_TARGET' | 'EVERY_TICK';
  readonly termination: 'FIRST_VALID_CONTACT' | 'AFTER_CONTACTS' | 'CONTINUE';
  readonly perTargetCooldownS?: number;
  readonly maxPierce?: number;
  readonly contactLimit?: number;
  /**
   * Projectile-only (2026-09-10 decision 10, Phase 4b; wired end-to-end
   * 2026-09-12): unlimited corridor pierce with per-pierce damage falloff,
   * mirrored unchanged from `Delivery`'s own `pierceFalloff` — see that
   * type's doc comment (`src/types.ts`). When present, the runtime treats
   * `maxPierce` as unbounded (`src/sim/runtime.ts` `spawnPersistentProjectile`)
   * regardless of any `maxPierce` also carried here.
   */
  readonly pierceFalloff?: PierceFalloff;
}

export type CanonicalLifecycleV2 =
  | { readonly kind: 'INSTANT' }
  | {
      readonly kind: 'PERSISTENT';
      readonly durationS: number;
      readonly mode?: 'PULSE' | 'CONTINUOUS';
      readonly collisionCheckIntervalS?: number;
      readonly hitIntervalS?: number;
      readonly targetPolicy?: 'SNAPSHOT_POINT' | 'KEEP_TARGET_HANDLE' | 'REQUERY_AT_EXECUTION';
    }
  /**
   * Gap "D" axis L — a caster-coupled-lifetime skill (channel / toggle /
   * upkeep). Unlike PERSISTENT's fixed `durationS`, a SUSTAINED skill runs
   * "while the caster sustains it", bounded by `maxSustainS` and by upkeep
   * against the resource pool (pricing model P-b — the envelope is finite, so
   * the existing burst/persistence pricing applies unchanged). Lowers to the
   * legacy `Skill.sustain` marker (`src/types.ts`, `SustainSpec`). See
   * `docs/superpowers/specs/2026-09-08-sustained-caster-coupled-skills.md`.
   * Position-following (aura/trail, axis P) is deferred and not expressible here.
   */
  | {
      readonly kind: 'SUSTAINED';
      readonly maxSustainS: number;
      readonly hitIntervalS: number;
      readonly end: import('../types.js').SustainEndPolicy;
    };

export interface SkillApplicationV2 {
  readonly placement: CanonicalPlacementV2;
  readonly acquisitionRangeM?: number;
  readonly geometry: CanonicalGeometryV2;
  readonly motion: CanonicalMotionV2;
  readonly contactPolicy: CanonicalContactPolicyV2;
  readonly lifecycle: CanonicalLifecycleV2;
}

export interface CanonicalConditionNodeV2 { readonly kind: 'condition'; readonly predicate: Extract<EffectNode, { kind: 'condition' }>['predicate']; readonly children: readonly CanonicalEffectNodeV2[]; }
export interface CanonicalTriggerNodeV2 { readonly kind: 'trigger'; readonly event: Extract<EffectNode, { kind: 'trigger' }>['event']; readonly selector?: Selector; readonly children: readonly CanonicalEffectNodeV2[]; }
export interface CanonicalSecondaryAttackNodeV2 { readonly kind: 'secondary_attack'; readonly application: SkillApplicationV2; readonly procPolicy?: 'blocked' | 'allowed'; readonly effects: readonly CanonicalEffectNodeV2[]; }
export type CanonicalEffectNodeV2 = LeafEffect | CanonicalConditionNodeV2 | CanonicalTriggerNodeV2 | CanonicalSecondaryAttackNodeV2;

export type CanonicalSkillV2Input = Omit<Skill, 'delivery' | 'effects' | 'sustain'> & { readonly schemaVersion: typeof CANONICAL_SKILL_V2_SCHEMA_VERSION; readonly application: SkillApplicationV2; readonly effects: readonly CanonicalEffectNodeV2[]; };
/** Only validateCanonicalSkillV2 can create this nominal, executable-ready data boundary. */
export type CanonicalSkillV2 = CanonicalSkillV2Input & { readonly [validatedCanonicalSkillV2]: true };
